/* ============================================================
   Dathent — Post-onboarding activation orchestrator
   Walks the user from chat completion → workspace-ready in 4 stages:
     1. preparing_plan        — running strategy.generate + weeklyPlan
     2. celebrate             — OnboardingCelebrate overlay (existing)
     3. connect_accounts      — inline social-connect button grid
     4. generating_first_post — running content.generateFirstPost
   On finish, calls onDone({ firstPostId, tour }) so the parent
   routes to /app/content/<id>/preview or /app?tour=1 accordingly.

   OAuth round-trip: when the user clicks Connect Instagram (etc),
   we stash this component's state in localStorage and set the
   ONBOARDING_SOCIAL_RETURN_KEY flag so App.jsx redirects the user
   back to /onboarding after OAuth. On mount we rehydrate phase +
   connectedPlatforms from URL params + stashed state so the user
   lands back exactly where they were.
   ============================================================ */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands.jsx';
import { api } from '../services/api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { OnboardingCelebrate } from './OnboardingCelebrate.jsx';
import { track, trackOnce } from '../lib/funnel.js';
import { getFunnelSource } from '../lib/funnelSource.js';
import SocialConnectConfirm from '../components/billing/SocialConnectConfirm.jsx';
import { wouldExceedIncluded, resolveSocialAddonState } from '../lib/socialAddon.js';
import { socialCapacityFrom } from '../lib/socialCapacity.js';
import { describeOAuthReturn, isConnectBlocked } from '../lib/onboardingConnect.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { platformDisplayName } from '../lib/platforms.js';
import { freePreviewState } from '../lib/freePreview.js';
import { freePreviewClientEnabled } from '../lib/flags.js';
import { freePublishingState } from '../lib/freePublishing.js';

const STASH_KEY = 'dathent_onboarding_activation_state';
const ONBOARDING_SOCIAL_RETURN_KEY = 'dathent_onboarding_social_oauth_return';

// Research-first onboarding — GA 2026-06-14 (default ON). New signups skip the
// blocking first-post generation: completion kicks the research run server-side
// and the dashboard Setup Journey drives research → strategy → first post
// asynchronously. Existing users are unaffected — the Setup Journey only engages
// when a fresh per-user JOURNEY_PENDING_KEY marker (<24h) is also present, which
// is set during this flow. Emergency off: localStorage 'dathent.research.onboarding' = '0'.
// Read LIVE (not at module load) so a console override works without a reload.
export function researchOnboardingEnabled() {
  try {
    return typeof window === 'undefined' || window.localStorage.getItem('dathent.research.onboarding') !== '0';
  } catch (_) {
    return true;
  }
}

// Per-user marker (suffixed with the user id) that a research-first
// onboarding just completed — the dashboard Setup Journey engages only while
// it is present and fresh, so established users never get a spurious journey.
export const JOURNEY_PENDING_KEY = 'dathent.research.journey.pending';

// Platforms we surface in the inline connect grid. Order matches what
// most users prioritize (visual first, conversation second). LinkedIn/X/
// TikTok/Threads are intentionally below the fold — fewer onboarding
// users start there, and the panel stays small on first scroll.
const PLATFORMS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'telegram', name: 'Telegram' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'threads', name: 'Threads' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'x', name: 'X (Twitter)' },
];

// Wait helper for backoff between retries.
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Structured frontend logger — matches the worker-side log shape so a
// flow can be read end-to-end via wrangler tail + browser devtools.
// All events flow under one trace id generated at activation start.
function logAct(trace, event, data = {}) {
  try {
    console.log(`[onboarding-activation] ${event}`, JSON.stringify({
      trace,
      event,
      ts: new Date().toISOString(),
      ...data
    }));
  } catch {
    // noop
  }
}
// Cheap, non-cryptographic trace id. We send it to workers via
// x-dathent-trace-id where the proxy picks it up.
function newTraceId() {
  return 'oa-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
}

// Strategy generation can race with D1's eventual-consistency after
// onboarding/complete commits the brand profile (worker B may read a
// stale snapshot before worker A's write replicates). We also see
// transient AI provider hiccups. Try a few times with growing backoff
// before giving up — the user shouldn't see "AI returned an invalid
// response" for a transient blip.
// (Removed `generateStrategyWithRetry` — strategy generation is now
// triggered inside content-worker's first-post handler instead of as
// a separate upfront call. The retry-with-backoff logic still lives
// in strategy-service.ts on the worker side.)

// Build a `firstPost` flat object from a raw content item — used when
// we resume into first_post_preview and need a payload shape that
// matches what `api.content.generateFirstPost` would return.
function extractFirstPostShape(content) {
  const item = content?.contentItem ?? content;
  const version = content?.activeVersion ?? {};
  const platform = (() => {
    const versionPlatforms = Object.keys(version.platformVersions || {})
      .filter((k) => !k.startsWith('__'));
    return versionPlatforms[0] || 'instagram';
  })();
  return {
    platform,
    format: item?.format || 'image_post',
    caption: version.caption || version.body || '',
    body: version.body || '',
    hook: version.hook || '',
    cta: version.cta || '',
    hashtags: Array.isArray(version.hashtags) ? version.hashtags : [],
    imagePrompt: null,
    reusedExisting: true,
  };
}

// Stash TTL — if more than 2 hours old, treat as stale and drop.
// Prevents an old broken activation from blocking the user across
// sessions (e.g., after a deploy that changed activation phases).
const STASH_TTL_MS = 2 * 60 * 60 * 1000;

function readStash(userId) {
  try {
    const raw = window.localStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;
    // Stash from before TTL tracking — ignore (it's pre-this-change).
    if (typeof parsed.savedAt !== 'number') {
      try { window.localStorage.removeItem(STASH_KEY); } catch {}
      return null;
    }
    // Account-scoped: a stash belongs to ONE user. If a DIFFERENT account is
    // signed in on this browser (or the stash predates userId tagging), drop
    // it so onboarding starts fresh instead of resuming the previous
    // account's activation step. (RequireAuth guarantees user is loaded
    // before onboarding mounts, so `userId` is reliable here.)
    if (parsed.userId !== (userId ?? null)) {
      try { window.localStorage.removeItem(STASH_KEY); } catch {}
      return null;
    }
    if (Date.now() - parsed.savedAt > STASH_TTL_MS) {
      try { window.localStorage.removeItem(STASH_KEY); } catch {}
      return null;
    }
    return parsed;
  } catch {
    try { window.localStorage.removeItem(STASH_KEY); } catch {}
    return null;
  }
}

function writeStash(state, userId) {
  try {
    window.localStorage.setItem(
      STASH_KEY,
      JSON.stringify({ ...state, userId: userId ?? null, savedAt: Date.now() })
    );
  } catch {}
}

function clearStash() {
  try { window.localStorage.removeItem(STASH_KEY); } catch {}
}

// Detect if the page was just redirected back from an OAuth flow.
// App.jsx forwards the OAuth result params (provider, connected, error,
// platform, limit, plan, keptBrand, limitReached) into
// /onboarding?continue=activation&provider=X&connected=true&... .
// error/limit/plan/keptBrand parsing is the pure, tested describeOAuthReturn
// helper (src/lib/onboardingConnect.js) — auth-worker's redirect appends
// &limit=<n>&plan=<key> alongside error=social_accounts_limit_reached so the
// connectLimitReached banner can be worded with a real number; the banner
// falls back to the live GET /social/accounts capacity, then to the
// NoCount copy, when neither resolved (never a hardcoded guess).
function readOAuthReturn() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('continue') !== 'activation') return null;
  const provider = params.get('provider') || params.get('platform');
  const connected = params.get('connected') === 'true';
  // Partial Meta multi-account connect: some accounts landed, the rest were
  // capped mid-batch — same param Settings.jsx's AccountsTab already reads.
  const limitReached = params.get('limitReached') === '1';
  return { provider, connected, limitReached, ...describeOAuthReturn(params) };
}

export function OnboardingActivation({ firstName, extracted, onDone, freshSetup = false }) {
  const { user } = useAuth() || {};
  return <ActivationSession key={user?.id ?? 'anon'} firstName={firstName} extracted={extracted} onDone={onDone} freshSetup={freshSetup} />;
}

function ActivationSession({ firstName, extracted, onDone, freshSetup }) {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const dash = useDashboardData() || {};
  const preview = freePreviewClientEnabled() && freePreviewState(dash.entitlements);
  const previewCohort = Boolean(preview && preview.mode !== 'hidden');
  const freePublishing = dash.entitlements?.accessState === 'free'
    || dash.readEntitlements?.accessState === 'free' || Boolean(freePublishingState(dash.entitlements));
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const userId = user?.id ?? null;
  // ----- restore state from a prior OAuth round-trip if applicable -----
  // Scoped to the current account so a different user on this browser never
  // resumes someone else's activation state.
  const stash = useRef(freshSetup ? null : readStash(userId)).current;
  const oauthReturn = useRef(readOAuthReturn()).current;
  // Start directly on connect_accounts — strategy generation used to
  // run here behind a "Preparing your content plan" loading screen but
  // it routinely took 30-90s and gave the user nothing they could see
  // or do. Strategy is now built inside the first-post generation
  // request, so we skip straight to the next user-facing step.
  const initialPhase = stash?.phase || 'connect_accounts';

  const [phase, setPhase] = useState(initialPhase);
  const [planError, setPlanError] = useState(null);
  const [firstPostError, setFirstPostError] = useState(null);
  const [connectedPlatforms, setConnectedPlatforms] = useState(new Set(stash?.connected || []));
  // Full account rows from /social/accounts — we need each account's
  // `id` to schedule a publish, since scheduler.create takes
  // socialAccountId per target. Refreshed when the connect-accounts
  // phase mounts.
  const [accountsList, setAccountsList] = useState([]);
  // Connections that existed BEFORE the user started this onboarding —
  // used by the UI to distinguish "Already linked" (visual softer hint)
  // vs "Linked" (just connected this session).
  const [preexistingConnections, setPreexistingConnections] = useState(new Set(stash?.preexisting || []));
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [socialConnectPlatform, setSocialConnectPlatform] = useState(null);
  // { limit, used, remaining, reached } from GET /social/accounts's live
  // `capacity` object — drives the disabled-Connect-buttons hint below
  // once the plan's connect-during-setup cap is reached (remaining === 0).
  const [capacity, setCapacity] = useState(null);
  const tourChoiceRef = useRef(stash?.tour || 'skip');
  // Trace id for the whole activation flow. Stashed so an OAuth round-trip
  // resumes with the same id and the worker logs remain correlated.
  const traceRef = useRef(stash?.trace || newTraceId());

  useEffect(() => {
    dash.ensureDashboardData?.().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- clean the URL once we've read OAuth return params -----
  useEffect(() => {
    if (!oauthReturn) return;
    try {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }, []);

  // Funnel (TASK-122): `oauthReturn` (read once at mount, above) is App.jsx's
  // authoritative signal that the OAuth callback actually succeeded — more
  // reliable than diffing connectedPlatforms/preexistingConnections, which
  // this component's own resume logic can re-baseline on the very same
  // remount (see the accounts.list effect below). Telegram doesn't redirect
  // the tab, so it never sets oauthReturn — a known gap, no client signal
  // exists for it here.
  useEffect(() => {
    if (oauthReturn?.connected && oauthReturn?.provider) {
      try { track('social_connected', { platform: oauthReturn.provider }); } catch { /* fail-silent */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface an honest reason when the OAuth round-trip came back WITHOUT a
  // connected account: either the plan's connect-during-setup capacity was
  // already reached (social_accounts_limit_reached — the free-plan cap is
  // 1, unchanged) or some other provider-side failure. Previously `error`
  // was never read here, so the user saw the connect grid unchanged with
  // no explanation at all.
  useEffect(() => {
    if (!oauthReturn?.error) return;
    logAct(traceRef.current, 'oauth_return.error', {
      error: oauthReturn.error,
      limit: oauthReturn.limit,
      plan: oauthReturn.plan,
      keptBrand: oauthReturn.keptBrand,
      provider: oauthReturn.provider,
    });
    if (oauthReturn.error === 'social_accounts_limit_reached') {
      setPlanError({ key: 'connectLimitReached', limit: oauthReturn.limit });
    } else {
      const platformLabel = PLATFORMS.find((p) => p.id === oauthReturn.provider)?.name || oauthReturn.provider || '';
      setPlanError({ key: 'connectFailedGeneric', platform: platformLabel });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Wire the activation trace id into every outbound API call so
  // worker logs (wrangler tail) can be matched up with the browser logs
  // by a single trace string. Cleared on unmount so the trace doesn't
  // leak into normal post-onboarding traffic. -----
  useEffect(() => {
    api.setDefaultHeader('x-dathent-trace-id', traceRef.current);
    logAct(traceRef.current, 'mount', { initialPhase, oauthReturn });
    return () => {
      api.clearDefaultHeader('x-dathent-trace-id');
    };
  }, []);

  // (Phase 1 removed — `strategy.generate` used to run here behind
  // a 30-90s "Preparing your content plan" loading screen. Strategy
  // is now bootstrapped inside the first-post generation request
  // (content-worker already has that fallback), so onboarding skips
  // the upfront strategy call entirely.)

  // ----- Phase 3: load already-connected accounts on mount -----
  useEffect(() => {
    if (phase !== 'connect_accounts') return;
    let cancelled = false;
    api.socialAccounts.list().then((res) => {
      if (cancelled) return;
      // m4 (fix-round): read defensively like Settings.jsx's AccountsTab
      // (res?.data unwrap) — the api client's response shape has varied by
      // call site historically, and this screen's original `res?.capacity`-
      // only read would silently drop capacity if it ever came back nested.
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const list = Array.isArray(payload?.accounts) ? payload.accounts : (Array.isArray(payload) ? payload : []);
      setAccountsList(list);
      setCapacity(payload?.capacity || null);
      const fresh = new Set(list.map((a) => String(a.platform || '').toLowerCase()).filter(Boolean));
      setConnectedPlatforms(fresh);
      // First time we see the live list, snapshot it as "pre-existing"
      // so anything connected DURING this onboarding stands out. If we
      // already have a stashed pre-existing set (from an OAuth round-trip
      // mid-session), keep it — that's the authoritative baseline.
      setPreexistingConnections((current) => current.size ? current : new Set(fresh));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [phase]);

  // Hold the generated first-post id so the welcome screen can route
  // the user to its preview as the final CTA.
  const [firstPostId, setFirstPostId] = useState(null);
  // Hold the full first-post payload so the inline preview screen can
  // render image + caption + platform without re-fetching.
  const [firstPostPayload, setFirstPostPayload] = useState(null);

  // Initialize firstPostId from stash so a refresh during/after first
  // post generation knows which content to show.
  useEffect(() => {
    if (stash?.firstPostId && !firstPostId) {
      setFirstPostId(stash.firstPostId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On resume into first_post_preview, the in-memory payload is empty —
  // re-fetch the content item by id so the preview has something to
  // render. Fire-and-forget; if it fails the preview shows the loading
  // image-placeholder state.
  useEffect(() => {
    if (phase !== 'first_post_preview') return;
    if (firstPostPayload) return; // already have it from same-session generation
    if (!firstPostId) return;
    let cancelled = false;
    api.content.get(firstPostId).then((res) => {
      if (cancelled || !res) return;
      // Wrap to match the shape produced by generateFirstPost so the
      // preview's payload-reader paths work the same way.
      setFirstPostPayload({ content: res, firstPost: extractFirstPostShape(res) });
    }).catch((err) => {
      logAct(traceRef.current, 'first_post.resume_fetch_failed', {
        firstPostId,
        error: err?.message || String(err)
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, firstPostId]);

  // ----- Persistence: stash enough state at every phase transition
  // (except the terminal welcome state) so a refresh / accidental
  // navigation away resumes exactly where the user was. The stash is
  // cleared by handleWelcomeDone when activation truly finishes.
  useEffect(() => {
    if (phase === 'welcome') return;
    writeStash({
      phase,
      connected: Array.from(connectedPlatforms),
      preexisting: Array.from(preexistingConnections),
      tour: tourChoiceRef.current,
      trace: traceRef.current,
      firstPostId,
    }, userId);
    // accountsList NOT included — it's re-fetched on resume, and stale
    // copies would be misleading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, connectedPlatforms, preexistingConnections, firstPostId]);

  // ----- Phase 4: kick off first-post generation -----
  useEffect(() => {
    if (phase !== 'generating_first_post') return;
    if (freePublishing) {
      setPhase('welcome');
      return;
    }
    let cancelled = false;
    const trace = traceRef.current;
    logAct(trace, 'first_post.start', {});
    const t0 = Date.now();

    // Up to 3 attempts. The most common failure mode is the D1 race
    // (brand profile not yet visible to content-worker) — the worker
    // now retries internally, but we keep a client-side retry too for
    // network blips. Retries skip on auth/quota errors.
    (async () => {
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        if (cancelled) return;
        try {
          // forceNew is true only on a truly FRESH activation (no
          // stash). On any resume — even if firstPostId hasn't been
          // captured yet — we let the worker reuse whatever it
          // produced last so we don't burn through the daily quota
          // re-generating the same post on every refresh.
          const isFreshActivation = !stash;
          logAct(trace, 'first_post.attempt', { attempt, freshActivation: isFreshActivation });
          const res = await api.content.generateFirstPost({
            includePremiumImage: true,
            forceNew: isFreshActivation,
          });
          if (cancelled) return;
          const id =
            res?.content?.contentItem?.id ||
            res?.content?.id ||
            res?.firstPost?.id ||
            res?.id ||
            null;
          logAct(trace, 'first_post.response', {
            attempt,
            hasId: Boolean(id),
            id,
            elapsedMs: Date.now() - t0,
            responseKeys: Object.keys(res || {}),
          });
          if (id) {
            setFirstPostId(id);
            setFirstPostPayload(res);
            // Funnel (TASK-122): the aha-moment the owner's diagnosis flags
            // as correctly placed BEFORE payment — the very first generated
            // post existing. trackOnce (not track) because a retry/resume of
            // this SAME effect can reach here more than once per user.
            try { trackOnce('first_generation', { attempt }); } catch { /* fail-silent */ }
            try { trackOnce('first_post_created', { source: getFunnelSource(), surface: 'onboarding_activation' }); } catch { /* fail-silent */ }
            // Land on the inline preview first so the user gets the
            // wow moment of seeing the post. Welcome confetti comes
            // AFTER they continue from the preview.
            setPhase('first_post_preview');
            return;
          }
          // 200 OK with no id — treat as a soft failure but still go
          // to welcome (CTA falls back to "Go to dashboard").
          logAct(trace, 'first_post.empty_success', { attempt });
          setFirstPostError(t('onboardingActivation.firstPostPreparing'));
          setPhase('welcome');
          return;
        } catch (err) {
          lastErr = err;
          const status = err?.status || 0;
          logAct(trace, 'first_post.error', {
            attempt,
            status,
            code: err?.code || null,
            message: err?.message || null
          });
          // Don't retry on auth/quota/rate-limit — those need either
          // user action or just time. Retrying a 429 just wastes another
          // slot of the user's daily budget for the same error.
          if (status === 401 || status === 402 || status === 403 || status === 429) break;
          if (attempt < 3) await sleep(800 * attempt);
        }
      }
      if (cancelled) return;
      // All attempts failed. Set an error message but STILL advance to
      // welcome — the user always gets the final celebration screen,
      // and the welcome CTA falls back to "Go to dashboard" when no
      // post id exists. They are never stranded on a loading screen.
      logAct(trace, 'first_post.failed_after_retries', {
        message: lastErr?.message || null,
        elapsedMs: Date.now() - t0
      });
      setFirstPostError(lastErr?.message || t('onboardingActivation.couldNotGenerate'));
      setPhase('welcome');
    })();
    return () => { cancelled = true; };
  }, [phase, freePublishing]);

  // RESILIENCE: first-post generation is a single long request (~up to 2 min).
  // If the user switches browser tabs, the browser can suspend/drop that
  // in-flight request and this screen stays stuck on "Generating…" even though
  // the backend actually finished (a manual reload then revealed the ready
  // post). When the tab becomes visible again we re-check with a REUSE call
  // (forceNew:false never regenerates — it returns whatever the worker already
  // produced) and advance to the preview, so the user never has to reload.
  useEffect(() => {
    if (phase !== 'generating_first_post' || freePublishing) return undefined;
    let cancelled = false;
    let recovering = false;
    const onVisible = async () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      if (cancelled || recovering) return;
      recovering = true;
      try {
        const res = await api.content.generateFirstPost({ includePremiumImage: true, forceNew: false });
        if (cancelled) return;
        const id =
          res?.content?.contentItem?.id ||
          res?.content?.id ||
          res?.firstPost?.id ||
          res?.id ||
          null;
        if (id) {
          logAct(traceRef.current, 'first_post.recovered_on_visible', { id });
          setFirstPostId(id);
          setFirstPostPayload(res);
          try { trackOnce('first_generation', { recovered: true }); } catch { /* fail-silent */ }
          try { trackOnce('first_post_created', { source: getFunnelSource(), surface: 'onboarding_activation' }); } catch { /* fail-silent */ }
          setPhase('first_post_preview');
        }
      } catch {
        // Swallow — the primary generation effect above owns error handling and
        // its own retries; this is only an opportunistic resume.
      } finally {
        recovering = false;
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVisible); };
  }, [phase, freePublishing]);

  const handleWelcomeDone = (choice) => {
    if (!mounted.current) return;
    // Funnel (TASK-122): the true end of the whole onboarding+activation
    // flow, whichever exit the user took from the final welcome screen.
    try { track('onboarding_completed', { choice: choice || 'dashboard', hasFirstPost: Boolean(firstPostId) }); } catch { /* fail-silent */ }
    clearStash();
    if (choice === 'open_post' && firstPostId) {
      onDone?.({ firstPostId, tour: false });
      return;
    }
    if (choice === 'tour') {
      onDone?.({ firstPostId: null, tour: true });
      return;
    }
    onDone?.({ firstPostId: null, tour: false });
  };

  // (Old onCelebrateDone removed — the welcome celebration is now the
  // final stage and uses handleWelcomeDone directly.)

  // ----- Connect button: stash state then redirect to OAuth -----
  const startConnect = async (platform, meta = {}) => {
    if (connectingPlatform || connectedPlatforms.has(platform)) return;
    setConnectingPlatform(platform);

    // Telegram returns a bot-setup LINK instead of redirecting the tab. A
    // window.open() that runs AFTER an await is treated as non-user-initiated
    // and silently blocked by the browser — which is why onboarding Telegram
    // connect "did nothing". Open a blank tab NOW, synchronously inside the
    // click gesture, and point it at the link once it resolves. Detach the
    // opener for safety. Non-telegram platforms keep tgWindow = null.
    let tgWindow = null;
    if (platform === 'telegram') {
      tgWindow = window.open('about:blank', '_blank');
      if (tgWindow) { try { tgWindow.opener = null; } catch (_) { /* noop */ } }
    }

    // Stash so we resume in the same phase + with the same connected set
    // when the OAuth callback redirects us back. Also preserve the
    // pre-existing baseline so the "Already linked" pill remains correct
    // after the round-trip, and the trace id so worker logs continue
    // under the same correlation key.
    writeStash({
      phase: 'connect_accounts',
      connected: Array.from(connectedPlatforms),
      preexisting: Array.from(preexistingConnections),
      tour: tourChoiceRef.current,
      trace: traceRef.current,
    }, userId);
    // App.jsx looks for this flag to route the OAuth callback back to
    // /onboarding instead of /app/settings.
    try { window.localStorage.setItem(ONBOARDING_SOCIAL_RETURN_KEY, '1'); } catch {}

    try {
      const res = await api.socialAccounts.connect(platform, {
        returnPath: '/app/settings/integrations',
        // Only true when this came from <SocialConnectConfirm>'s onContinue
        // AND it was actually showing the priced overage sheet.
        overageAck: meta?.overageAck === true,
      });
      // socialAccounts.connect uses window.location.assign for OAuth
      // platforms (instagram, threads, meta, tiktok, x). For Telegram
      // it returns a `link.url` we should open in a new tab. Either
      // way, after this the page is navigating away — leave state in
      // localStorage so we can resume.
      // Telegram's createTelegramLink() returns { linkUrl }; OAuth platforms
      // already navigated the tab inside connect() and return no link here.
      const tgUrl = res?.linkUrl || res?.link?.url || null;
      if (tgUrl) {
        if (tgWindow) tgWindow.location.href = tgUrl;
        else window.open(tgUrl, '_blank', 'noopener');
        // Telegram doesn't navigate the current tab — accounts.list polling
        // settles the connected state once the bot is added to a channel.
        setConnectingPlatform(null);
      } else if (tgWindow) {
        // No link came back — don't leave a dangling blank tab open.
        tgWindow.close();
      }
      // For OAuth platforms, navigation already happened.
    } catch (err) {
      console.error('[activation] connect failed', err);
      if (tgWindow) tgWindow.close();
      setConnectingPlatform(null);
      clearStash();
    }
  };

  const handleConnect = (platform) => {
    if (connectingPlatform || connectedPlatforms.has(platform)) return;
    // TASK-329: onboarding social-connect confirmation stays (owner decision
    // — let them connect, billing adapts), generalised beyond the Scheduler
    // to any plan the org is about to exceed the included count on.
    if (dash.entitlements?.plan === 'posting' || wouldExceedIncluded(social)) {
      setSocialConnectPlatform(platform);
      return;
    }
    startConnect(platform);
  };

  const handleContinueFromConnect = () => {
    if (!mounted.current) return;
    // Funnel (TASK-122): "skip for now" — the user left connect_accounts
    // without linking a single platform. Owner's cohort read names social
    // connection as the strongest trial-conversion predictor, so this is a
    // meaningful drop-off point to see in the funnel, not just "continued".
    if (connectedPlatforms.size === 0) {
      try { track('onboarding_skipped', { step: 'connect_accounts' }); } catch { /* fail-silent */ }
    }
    clearStash();
    // Owner decision 2026-09-07 (item 4): Posting has no research/strategy/
    // AI first-post pipeline at all (skipped_plan) — straight to the
    // celebrate/welcome screen, no activation narration and no generation
    // call of any kind. This used to fall into the research-first branch
    // below regardless of plan, arming SetupJourneyWidget's dashboard hero
    // ("Your workspace is coming to life" / "Researching your market…")
    // for an account that will never produce a research report — the card
    // got stuck narrating a pipeline posting doesn't have.
    if (dash.entitlements?.plan === 'posting' || freePublishing) {
      setPhase('welcome');
      return;
    }
    // Research-first flow: no blocking generation here — celebrate and land
    // on the dashboard, where the Setup Journey shows the async chain.
    if (researchOnboardingEnabled()) {
      try {
        window.localStorage.setItem(`${JOURNEY_PENDING_KEY}.${userId ?? 'anon'}`, String(Date.now()));
      } catch (_) { /* private mode */ }
      setPhase('welcome');
      return;
    }
    setPhase('generating_first_post');
  };

  // B2 (fix-round): resolved through resolveSocialAddonState — the SAME one
  // gate Settings.jsx's AccountsTab reads (lib/socialAddon.js) — instead of
  // this screen re-deriving billable/reason/null-price checks locally. Feeds
  // both handleConnect's existing wouldExceedIncluded(social) check above and
  // the Connect-button disable gate passed down to ConnectAccountsScreen
  // below, so the two can never disagree about which mode this org is in.
  const social = dash.entitlements?.socialAccounts || null;
  const socialAddonState = resolveSocialAddonState(social);

  // Live connect-during-setup capacity (TASK: same helper Settings.jsx uses
  // for its own connect grid) — reached === true is NOT by itself a reason
  // to disable the remaining Connect buttons below (see addonState/B2): a
  // billable ('priced') org at its included count can still connect as a
  // paid Scheduler add-on overage through handleConnect's existing
  // <SocialConnectConfirm> path above.
  const capacityInfo = socialCapacityFrom({ capacity, entitlements: dash.entitlements, accounts: accountsList });

  // ============================================================
  //  Render
  // ============================================================
  if (phase === 'connect_accounts') {
    return (
      <>
        <ConnectAccountsScreen
        connected={connectedPlatforms}
        preexistingConnections={preexistingConnections}
        accounts={accountsList}
        orgDefaultLanguage={extracted?.language || 'en'}
        connecting={connectingPlatform}
        onConnect={handleConnect}
        onContinue={handleContinueFromConnect}
        planError={planError}
        capacity={capacityInfo}
        addonState={socialAddonState}
        preview={previewCohort && !freePublishing}
        />
        <SocialConnectConfirm
          platform={socialConnectPlatform}
          entitlements={dash.entitlements}
          onCancel={() => setSocialConnectPlatform(null)}
          onContinue={(meta) => {
            const platform = socialConnectPlatform;
            setSocialConnectPlatform(null);
            startConnect(platform, meta);
          }}
        />
      </>
    );
  }
  if (phase === 'generating_first_post') {
    return <GeneratingFirstPostScreen error={firstPostError} />;
  }
  if (phase === 'first_post_preview') {
    return (
      <FirstPostPreviewScreen
        firstPostId={firstPostId}
        payload={firstPostPayload}
        connectedPlatforms={connectedPlatforms}
        accountsList={accountsList}
        onContinue={() => setPhase('welcome')}
      />
    );
  }
  if (phase === 'welcome') {
    // The welcome celebration IS the final stage now — it shows confetti
    // and pills like before, but with CTAs that route the user to their
    // freshly-generated post (or dashboard).
    return (
      <OnboardingCelebrate
        firstName={firstName}
        extracted={extracted}
        firstPostId={firstPostId}
        onDone={handleWelcomeDone}
        preview={previewCohort}
      />
    );
  }
  return null;
}

/* ---------------- Stage screens ---------------- */

// Tick through a 3-step checklist on a wall-clock cadence. Backend
// doesn't stream sub-progress, so we simulate phases that match what's
// genuinely happening: building strategy (~15-25s) → laying out
// content direction (visible mid-loading) → tuning for platforms.
function useChecklistProgress(stepCount, totalEstimateMs = 30000) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (stepCount <= 1) return undefined;
    const stepMs = Math.max(2500, Math.floor(totalEstimateMs / stepCount));
    const id = setInterval(() => {
      setStep((s) => Math.min(stepCount - 1, s + 1));
    }, stepMs);
    return () => clearInterval(id);
  }, [stepCount, totalEstimateMs]);
  return step;
}

function ChecklistRow({ idx, activeIdx, label }) {
  let state = 'pending';
  if (idx < activeIdx) state = 'done';
  else if (idx === activeIdx) state = 'active';
  return (
    <li>
      <span className={`oa-check oa-check-${state}`}>
        {state === 'active' ? <Spinner /> : state === 'done' ? <I.check size={11} /> : null}
      </span>
      {label}
    </li>
  );
}

// (Removed PreparingPlanScreen — phase no longer exists. Strategy
// generation moved inside the first-post handler, so the only
// loading screen the user sees is GeneratingFirstPostScreen, which
// communicates strategy + post + image work in one place.)

// Languages available for per-account override. Keys match
// PUBLISH_LANGUAGES in Settings.jsx and the onboarding LanguagePicker.
const ACCOUNT_LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'uk', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'it', label: 'Italiano' },
  { code: 'pl', label: 'Polski' },
  { code: 'cs', label: 'Čeština' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'sv', label: 'Svenska' },
  { code: 'lt', label: 'Lietuvių' },
  { code: 'lv', label: 'Latviešu' },
];

function ConnectAccountsScreen({ connected, connecting, onConnect, onContinue, planError, preexistingConnections, accounts, orgDefaultLanguage, capacity, addonState, preview = false }) {
  const { t } = useTranslation();
  const connectedCount = connected?.size || 0;
  // B2 (fix-round): mirrors Settings.jsx AccountsTab's capacityBlocked gate —
  // reached alone is NOT a block. A billable ('priced') org at its included
  // count keeps its Connect buttons live so the paid Scheduler add-on
  // overage path (handleConnect -> <SocialConnectConfirm>) still works.
  const capacityBlocked = isConnectBlocked(capacity, addonState);
  // planError.limit is sourced from THIS round-trip's URL (auth-worker's
  // redirect appends &limit=<n> on social_accounts_limit_reached) and is
  // available the instant the page lands, before the accounts fetch below
  // even starts — prefer it; fall back to the live GET /social/accounts
  // capacity once it resolves. Never hardcode a guess: when neither source
  // has a number yet, capacityLimit stays null and the copy below switches
  // to the NoCount variant instead of rendering a fabricated count.
  const capacityLimit = planError?.limit ?? capacity?.limit ?? null;
  const capacityHint = capacityBlocked
    ? (capacityLimit != null
        ? t('onboardingActivation.connectLimitReached', { limit: capacityLimit })
        : t('onboardingActivation.connectLimitReachedNoCount'))
    : undefined;
  // Newly-linked-this-session vs already-linked-before-onboarding. We
  // surface the distinction so the user understands they didn't connect
  // 6 accounts during onboarding — those were already there from prior
  // sessions. Reduces "wait, why is everything Linked?" confusion.
  const isPreexisting = (platform) => preexistingConnections?.has(platform);
  // Local cache for in-flight saves so the dropdown reacts immediately
  // without waiting for the round-trip.
  const [savingLang, setSavingLang] = useState(null);
  const [accountLangOverrides, setAccountLangOverrides] = useState({});
  const handleAccountLangChange = async (accountId, code) => {
    setSavingLang(accountId);
    setAccountLangOverrides((cur) => ({ ...cur, [accountId]: code }));
    try {
      // null = inherit org default; a code = per-account override.
      await api.socialAccounts.updateLanguage(accountId, code || null);
    } catch (err) {
      console.warn('[activation] per-account language save failed', err);
    } finally {
      setSavingLang(null);
    }
  };
  const orgLangLabel = ACCOUNT_LANG_OPTIONS.find((l) => l.code === orgDefaultLanguage)?.label
    || (orgDefaultLanguage ? orgDefaultLanguage.toUpperCase() : 'English');
  const langPickerAccounts = (accounts || []).filter((a) => a?.id);
  return (
    <div className="oa-stage oa-stage-connect">
      <div className="oa-bg" />
      <div className="oa-card oa-card-wide">
        <div className="oa-orb">
          <img src="/logo.png" alt="" width={44} height={44} style={{ borderRadius: '50%' }} />
        </div>
        <h2 className="oa-title">{t('onboardingActivation.connectTitle')}</h2>
        <p className="oa-sub">
          {t(preview ? 'freePreview.connectSub' : 'onboardingActivation.connectSub')}
        </p>

        {/* B3 (fix-round): exactly one banner at a time — an explicit
            OAuth-return planError (a specific failure from THIS round-trip)
            always wins over the general "you're at capacity" hint, which
            only fires when there's no more specific error to show. Visible
            in-page text either way, never a title-only tooltip. */}
        {planError ? (
          <div className="oa-banner oa-banner-warn">
            <I.alert size={14} />{' '}
            {planError.key === 'connectLimitReached'
              ? (capacityLimit != null
                  ? t('onboardingActivation.connectLimitReached', { limit: capacityLimit })
                  : t('onboardingActivation.connectLimitReachedNoCount'))
              : t('onboardingActivation.connectFailedGeneric', { platform: planError.platform })}
          </div>
        ) : capacityBlocked ? (
          <div className="oa-banner oa-banner-warn">
            <I.alert size={14} />{' '}
            {capacityHint}
          </div>
        ) : null}

        {/* Unified channel list — minimalist single-line rows. Connected
            accounts first, then "Connect" rows for platforms not linked.
            Per-account language saves globally (also shows in Settings). */}
        <ul className="oa-channel-list">
          {(accounts || []).map((a) => {
            const platform = String(a?.platform || '').toLowerCase();
            const Icon = BrandIcons[platform];
            const currentLang =
              accountLangOverrides[a.id] !== undefined
                ? accountLangOverrides[a.id]
                : (a.publishLanguage || '');
            const platformLabel = (PLATFORMS.find((p) => p.id === platform)?.name) || platform;
            const display = a.displayName || a.handle || a.username || platformLabel;
            return (
              <li key={a.id} className="oa-row">
                <span className={`oa-row-ic oa-net-${platform}`}>
                  {Icon ? <Icon size={13} /> : <I.globe size={12} />}
                </span>
                <span className="oa-row-text">
                  <span className="oa-row-name">{display}</span>
                  <span className="oa-row-sub">{platformLabel}</span>
                </span>
                <label className="oa-row-lang" title={t('onboardingActivation.publishLanguageTitle')}>
                  <span className={`oa-row-lang-code${currentLang ? ' is-override' : ''}`}>
                    {currentLang
                      ? currentLang.toUpperCase()
                      : (orgDefaultLanguage || 'en').toUpperCase()}
                  </span>
                  <I.chevDown size={10} />
                  <select
                    value={currentLang || ''}
                    onChange={(e) => handleAccountLangChange(a.id, e.target.value || null)}
                    disabled={savingLang === a.id}
                  >
                    <option value="">{t('onboardingActivation.defaultLangOption', { lang: orgLangLabel })}</option>
                    {ACCOUNT_LANG_OPTIONS.map((l) => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                  </select>
                  {savingLang === a.id && <Spinner />}
                </label>
              </li>
            );
          })}
          {PLATFORMS.filter((p) => !connected?.has(p.id)).map((p) => {
            const isConnecting = connecting === p.id;
            const Icon = BrandIcons[p.id];
            return (
              <li key={p.id} className="oa-row is-empty">
                <span className={`oa-row-ic oa-row-ic-muted`}>
                  {Icon ? <Icon size={13} /> : <I.globe size={12} />}
                </span>
                <span className="oa-row-text">
                  <span className="oa-row-name oa-row-name-muted">{p.name}</span>
                </span>
                <button
                  type="button"
                  className="oa-row-connect"
                  disabled={isConnecting || capacityBlocked}
                  title={capacityBlocked ? capacityHint : undefined}
                  onClick={() => onConnect(p.id)}
                >
                  {isConnecting ? <><Spinner /> {t('onboardingActivation.opening')}</> : <>{t('onboardingActivation.connect')}</>}
                </button>
              </li>
            );
          })}
        </ul>

        {(accounts || []).length > 0 && (
          <p className="oa-row-hint">
            {t('onboardingActivation.defaultLanguageLabel')} <strong>{orgLangLabel}</strong> {t('onboardingActivation.defaultLanguageHint')}
          </p>
        )}

        <div className="oa-foot">
          <span className="oa-foot-meta">
            {connectedCount > 0
              ? t('onboardingActivation.connectedCount', { count: connectedCount, total: PLATFORMS.length })
              : t('onboardingActivation.noAccountsLinked')}
          </span>
          <button type="button" className="btn btn-primary btn-sm" onClick={onContinue}>
            {connectedCount > 0 ? t('onboardingActivation.continue') : t('onboardingActivation.skipForNow')} <I.arrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function GeneratingFirstPostScreen({ error }) {
  const { t } = useTranslation();
  // 4 steps now — strategy build folded in because we no longer
  // generate it upfront. First-post handler triggers it internally
  // when a brand has none yet, so the wait happens here.
  const items = [
    t('onboardingActivation.stepBuildStrategy'),
    t('onboardingActivation.stepWriteCopy'),
    t('onboardingActivation.stepGenerateVisual'),
    t('onboardingActivation.stepTunePlatforms'),
  ];
  const activeIdx = useChecklistProgress(items.length, 100000);
  // Stage label that morphs with the active step — mirrors what the
  // existing PostPreview generating-state shows in the Library when
  // a job is still running, so users see consistent vocabulary.
  const stage = error
    ? t('onboardingActivation.stageAlmostThere')
    : activeIdx === 0 ? t('onboardingActivation.stageBuildingStrategy')
    : activeIdx === 1 ? t('onboardingActivation.stageWritingDraft')
    : activeIdx === 2 ? t('onboardingActivation.stageGeneratingImage')
    : t('onboardingActivation.stageTuningPlatforms');

  if (error) {
    return (
      <div className="oa-stage">
        <div className="oa-bg" />
        <div className="oa-card">
          <div className="oa-orb">
            <img src="/logo.png" alt="" width={44} height={44} style={{ borderRadius: '50%' }} />
          </div>
          <h2 className="oa-title">{t('onboardingActivation.almostThereTitle')}</h2>
          <p className="oa-sub">
            {t('onboardingActivation.firstPostSnag', { error })}
          </p>
        </div>
      </div>
    );
  }

  // The phone-shaped preview only makes sense once we're past the
  // strategy phase (activeIdx >= 1). During the initial "Building
  // strategy" tick there's nothing post-shaped to show yet, so we
  // render the compact checklist on its own and swap to the phone
  // when the post actually starts taking shape.
  const showPhone = activeIdx >= 1;

  if (!showPhone) {
    return (
      <div className="oa-stage">
        <div className="oa-bg" />
        <div className="oa-card">
          <div className="oa-orb">
            <img src="/logo.png" alt="" width={44} height={44} style={{ borderRadius: '50%' }} />
          </div>
          <div className="pp-generating-status" aria-hidden="true">
            <span className="pp-generating-pulse-dot" />
            <span className="pp-generating-eyebrow">{t('onboardingActivation.preparing')}</span>
          </div>
          <h2 className="oa-title" style={{ marginTop: 8 }}>
            {stage}<span className="pp-generating-dots" aria-hidden="true">…</span>
          </h2>
          <p className="oa-sub">
            {t('onboardingActivation.preparingSub')}
          </p>
          <ul className="oa-checklist">
            {items.map((label, i) => (
              <ChecklistRow key={i} idx={i} activeIdx={activeIdx} label={label} />
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // Phone-frame loader — reuses pp-* classes from styles/post-preview.css
  // so the in-onboarding generation view shares vocabulary with the
  // Library's PostPreview generating-state. The user sees the SAME
  // animation they'd see if they navigated to /app/post/<id> on a
  // still-generating post.
  return (
    <div className="oa-stage oa-stage-generate">
      <div className="oa-bg" />
      <div className="oa-gen-wrap">
        <div className="pp-phone oa-phone">
          <div className="pp-phone-shell">
            <div className="pp-phone-notch" />
            <div className="pp-phone-screen pp-phone-screen-loading">
              <div className="pp-phone-status">
                <span>9:41</span>
                <span className="pp-phone-status-r">•••</span>
              </div>
              <div className="pp-loading-header" aria-hidden="true">
                <div className="pp-loading-avatar" />
                <div className="pp-loading-handle">
                  <div className="pp-loading-pill pp-loading-pill-w50" />
                  <div className="pp-loading-pill pp-loading-pill-w30" />
                </div>
              </div>
              <div className="pp-loading-media" aria-hidden="true">
                <div className="pp-generating-spinner" />
              </div>
              <div className="pp-loading-body" aria-hidden="true">
                <div className="pp-loading-pill pp-loading-pill-w90" />
                <div className="pp-loading-pill pp-loading-pill-w70" />
                <div className="pp-loading-pill pp-loading-pill-w50" />
              </div>
              <div className="pp-phone-home" />
            </div>
          </div>
        </div>

        <div className="oa-gen-side">
          <div className="pp-generating-status" aria-hidden="true">
            <span className="pp-generating-pulse-dot" />
            <span className="pp-generating-eyebrow">{t('onboardingActivation.generating')}</span>
          </div>
          <h2 className="pp-generating-h">
            {stage}<span className="pp-generating-dots" aria-hidden="true">…</span>
          </h2>
          <p className="pp-generating-sub">
            {t('onboardingActivation.generatingSub')}
          </p>
          <ul className="oa-checklist oa-checklist-stack">
            {items.map((label, i) => (
              <ChecklistRow key={i} idx={i} activeIdx={activeIdx} label={label} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="oa-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// (Removed: FirstPostReadyScreen and the subscription teaser. The
// welcome celebration at the end of the flow is now the single final
// screen, and the "Open my first post" CTA lives there. The plan-upsell
// will live on the main platform UI, not inside onboarding.)

// In-flow preview of the freshly-generated first post. Uses the
// existing PostPreview phone-frame CSS (`pp-phone-*`, `pp-app-h`,
// `pp-author`, etc.) so the preview looks like the one users see
// in the full PostPreview screen — not a one-off ugly card.
// Allowed platform set — matches the backend normalize list. Order
// dictates the tab strip ordering when multiple platforms qualify.
const PREVIEWABLE_PLATFORMS = ['instagram', 'facebook', 'telegram', 'threads', 'tiktok', 'linkedin', 'x'];

function FirstPostPreviewScreen({ firstPostId, payload, connectedPlatforms, accountsList, onContinue }) {
  const { t } = useTranslation();
  // Extract display fields from the payload. The backend returns BOTH
  // `content` (full record with activeVersion) and `firstPost` (a flat
  // convenience object). Combine both for the most reliable read.
  const first = payload?.firstPost ?? {};
  const content = payload?.content ?? {};
  const item = content.contentItem ?? content;
  const version = content.activeVersion ?? {};
  // The content-store puts the actual URL right on the version row
  // (`imageUrl`, `thumbnailUrl`, `mediaUrls[]`).
  const imageUrl =
    version.imageUrl ||
    version.thumbnailUrl ||
    (Array.isArray(version.mediaUrls) ? version.mediaUrls[0] : null) ||
    item.imageUrl ||
    item.image_url ||
    item.thumbnailUrl ||
    null;
  const baseCaption = String(first.caption || first.body || version.body || version.caption || '').trim();
  const hashtags = Array.isArray(first.hashtags) ? first.hashtags : [];

  // Platform-specific caption variants — backend generates one per
  // requested platform in a single AI call. Filter to platforms the
  // user has actually connected, in canonical order.
  const platformVersions = (version.platformVersions && typeof version.platformVersions === 'object')
    ? version.platformVersions
    : {};
  const previewablePlatforms = PREVIEWABLE_PLATFORMS.filter((p) => connectedPlatforms?.has(p));
  // Fall back to the post's primary platform if user has no connected
  // accounts yet (the preview still needs SOMEthing to show).
  const fallbackPlatform = (first.platform || 'instagram').toLowerCase();
  const previewPlatforms = previewablePlatforms.length ? previewablePlatforms : [fallbackPlatform];

  const [previewPlatform, setPreviewPlatform] = useState(previewPlatforms[0]);
  // Caption for the currently-previewed platform: prefer the AI's
  // per-platform variant; fall back to the generic caption.
  const captionForPreview = (() => {
    const v = platformVersions[previewPlatform];
    if (typeof v === 'string' && v.trim()) return v.trim();
    return baseCaption;
  })();

  // Per-platform display handle from the connected accounts list.
  const accountForPreview = (accountsList || []).find(
    (a) => String(a?.platform || '').toLowerCase() === previewPlatform
  );
  const username =
    accountForPreview?.displayName ||
    accountForPreview?.handle ||
    accountForPreview?.username ||
    item.brandHandle ||
    item.brandName ||
    'your_brand';
  const initial = (username || 'y').replace(/^@/, '').charAt(0).toUpperCase();

  const connectedList = Array.from(connectedPlatforms || []);
  const hasConnected = connectedList.length > 0;
  // We ALWAYS generate the first post in Instagram format, but only dress the
  // preview in Instagram chrome (action bar / Follow / platform tabs) when the
  // user actually connected Instagram. Otherwise show a neutral card so we never
  // claim a network they didn't choose — it reads as "your post", not "Instagram".
  const igConnected = connectedList.some((p) => String(p).toLowerCase() === 'instagram');
  const neutral = !igConnected;
  // Multi-select state for which channels to publish to. Default:
  // everything connected pre-selected — most users want to publish to
  // all their accounts and we save them a click.
  const [selectedTargets, setSelectedTargets] = useState(() => new Set(connectedList));
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const toggleTarget = (p) => {
    setSelectedTargets((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  // Publish handler — scheduler.create takes contentVersionId AND a
  // socialAccountId per target (platform alone isn't enough; a user
  // can have multiple accounts on the same platform). We pull both
  // from the payload + accountsList passed down via props.
  const handlePublish = async () => {
    if (publishing || selectedTargets.size === 0 || !firstPostId) return;
    const versionId = payload?.content?.activeVersion?.id;
    if (!versionId) {
      setPublishError(t('onboardingActivation.noActiveVersion'));
      return;
    }
    // Resolve socialAccountId for each selected platform — pick the
    // FIRST account on that platform (most users only have one per
    // network during onboarding; future iteration: per-account checks).
    const targets = Array.from(selectedTargets).flatMap((platform) => {
      const acct = accountsList.find((a) => String(a.platform || '').toLowerCase() === platform);
      if (!acct?.id) return [];
      return [{ platform, socialAccountId: acct.id }];
    });
    if (targets.length === 0) {
      setPublishError(t('onboardingActivation.noConnectedAccounts'));
      return;
    }
    setPublishing(true);
    setPublishError(null);
    try {
      // mode='publish_now' tells scheduler-worker to dispatch the
      // targets IMMEDIATELY (no cron wait). Status transitions to
      // 'publishing' right away — user lands on Post Preview already
      // in publishing-state instead of "Scheduled" with a stale CTA.
      const scheduledAt = new Date().toISOString();
      await api.scheduler.create({
        contentItemId: firstPostId,
        contentVersionId: versionId,
        scheduledAt,
        mode: 'publish_now',
        targets: targets.map((t) => ({ ...t, priority: 'now', scheduledAt })),
      });
      // Funnel (TASK-122): publishing the first-ever generated post, straight
      // from onboarding — the most common real "first publish" path. Later
      // publishes from Library/Edit Post are NOT instrumented (see TASK-122
      // PR notes: those call sites are scattered across several large
      // screens sharing api.scheduler.create, out of scope for this pass).
      try { track('first_publish', { platforms: targets.length }); } catch { /* fail-silent */ }
      onContinue();
    } catch (err) {
      console.error('[activation] publish failed', err);
      setPublishError(err?.message || t('onboardingActivation.couldNotPublish'));
      setPublishing(false);
    }
  };

  return (
    <div className="oa-stage oa-stage-preview">
      <div className="oa-bg" />
      <div className="oa-preview-wrap">
        <div className="oa-preview-head">
          <h2 className="oa-title">{t('onboardingActivation.firstPostReadyTitle')}</h2>
          <p className="oa-sub">
            {t('onboardingActivation.firstPostReadySub')}
          </p>
        </div>

        {/* Per-platform preview switcher. The AI generated a tuned
            caption variant for each connected channel — these tabs
            let the user flip between them and see how the post will
            look on Instagram vs Telegram vs Facebook, etc. */}
        {!neutral && previewPlatforms.length > 1 && (
          <div className="oa-preview-tabs" role="tablist">
            {previewPlatforms.map((p) => {
              const Icon = BrandIcons[p];
              const label = (PLATFORMS.find((pl) => pl.id === p)?.name) || p;
              const isActive = previewPlatform === p;
              return (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`oa-preview-tab${isActive ? ' is-active' : ''}`}
                  onClick={() => setPreviewPlatform(p)}
                >
                  <span className={`oa-publish-ic oa-net-${p}`}>
                    {Icon ? <Icon size={11} /> : null}
                  </span>
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Phone-frame preview using the same CSS as the full
            PostPreview screen — so the inline preview reads as the
            real thing, not a rough card. */}
        <div className="pp-phone oa-phone">
          <div className="pp-phone-shell">
            <div className="pp-phone-notch" />
            <div className={`pp-phone-screen pp-platform-${previewPlatform}`}>
              <div className="pp-phone-status">
                <span className="mono">9:41</span>
                <span className="pp-phone-status-r">
                  <svg width="14" height="9" viewBox="0 0 14 9" fill="currentColor">
                    <circle cx="1.5" cy="7.5" r="1.5" />
                    <circle cx="5.5" cy="6" r="1.5" />
                    <circle cx="9.5" cy="3.5" r="1.5" />
                  </svg>
                  <svg width="13" height="9" viewBox="0 0 13 9" fill="currentColor">
                    <path d="M0 4.5L6.5 0L13 4.5L11.5 6L6.5 2L1.5 6L0 4.5Z" />
                  </svg>
                  <svg width="18" height="9" viewBox="0 0 18 9" fill="none" stroke="currentColor" strokeWidth="0.8">
                    <rect x="0.4" y="0.4" width="14" height="8.2" rx="1.5" />
                    <rect x="2" y="2" width="11" height="5" rx="0.5" fill="currentColor" />
                    <rect x="15.2" y="3" width="1.4" height="3" rx="0.5" fill="currentColor" />
                  </svg>
                </span>
              </div>

              {/* IG chrome — modeled on PostPreview.jsx IGChrome */}
              <div className="pp-app-h">
                <button type="button"><I.chevLeft size={20} /></button>
                <div className="pp-ig-title">{username}</div>
                <button type="button"><I.dots size={18} /></button>
              </div>
              <div className="pp-author">
                <div className="pp-author-avatar" aria-hidden="true">{initial}</div>
                <div className="pp-author-text">
                  <div className="pp-author-name">{username.replace(/^@/, '')}</div>
                  <div className="pp-author-sub">{t('onboardingActivation.draftPost')}</div>
                </div>
                {!neutral && (
                  <button className="pp-follow" type="button">{t('onboardingActivation.follow')}</button>
                )}
              </div>
              <div className="oa-phone-media">
                {imageUrl ? (
                  <img src={imageUrl} alt={t('onboardingActivation.generatedPostAlt')} />
                ) : (
                  <div className="oa-phone-media-empty">
                    <Spinner /> <span>{t('onboardingActivation.imageRendering')}</span>
                  </div>
                )}
              </div>
              {!neutral && (
                <div className="pp-ig-actions">
                  <span><I.heart size={22} /></span>
                  <span><I.message size={22} /></span>
                  <span><I.send size={22} /></span>
                  <span style={{ marginLeft: 'auto' }}><I.bookmark size={22} /></span>
                </div>
              )}
              <div className="oa-phone-caption">
                <strong>{username.replace(/^@/, '')}</strong>{' '}
                <span style={{ whiteSpace: 'pre-wrap' }}>{captionForPreview}</span>
                {/* Hashtags only on platforms that use them. Telegram
                    doesn't, and our backend already strips them
                    inside platformVersions for those platforms. */}
                {hashtags.length > 0 && previewPlatform !== 'telegram' && (
                  <div className="oa-ig-tags">
                    {hashtags.slice(0, 8).map((tag, i) => (
                      <span key={i} className="oa-ig-tag">#{String(tag).replace(/^#/, '')}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="pp-phone-home" />
            </div>
          </div>
        </div>

        {/* Multi-select publish row + foot. We ONLY show publish UI
            when the user has connected channels — if not, the only
            path forward is "Save as draft & continue". */}
        <div className="oa-preview-actions">
          {hasConnected && (
            <div className="oa-publish-select">
              <span className="oa-publish-l">{t('onboardingActivation.publishTo')}</span>
              <div className="oa-publish-checks">
                {connectedList.map((p) => {
                  const Icon = BrandIcons[p];
                  const label = platformDisplayName(p);
                  const checked = selectedTargets.has(p);
                  return (
                    <label
                      key={p}
                      className={`oa-publish-check${checked ? ' is-on' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTarget(p)}
                        disabled={publishing}
                      />
                      <span className={`oa-publish-ic oa-net-${p}`}>
                        {Icon ? <Icon size={13} /> : null}
                      </span>
                      <span className="oa-publish-name">{label}</span>
                      {checked && <I.check size={12} className="oa-publish-tick" />}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {publishError && (
            <div className="oa-banner oa-banner-warn">
              <I.alert size={14} /> {publishError}
            </div>
          )}

          <div className="oa-preview-foot">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onContinue}
              disabled={publishing}
            >
              {hasConnected ? t('onboardingActivation.publishLater') : t('onboardingActivation.saveAsDraft')}
            </button>
            {hasConnected && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handlePublish}
                disabled={publishing || selectedTargets.size === 0}
              >
                {publishing ? (
                  <><Spinner /> {t('onboardingActivation.publishing')}</>
                ) : selectedTargets.size === 0 ? (
                  t('onboardingActivation.selectAtLeastOne')
                ) : (
                  <>{t('onboardingActivation.publishToChannels', { count: selectedTargets.size })} <I.arrowRight size={12} /></>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingActivation;
